import type { DriveStep, Side, Alignment } from 'driver.js'
import type { TFunction } from 'i18next'

export interface TourStep extends DriveStep {
  /** Route to navigate to before showing this step */
  route?: string
}

function step(
  element: string,
  titleKey: string,
  descKey: string,
  t: TFunction,
  opts?: { side?: Side; align?: Alignment; route?: string }
): TourStep {
  return {
    element,
    route: opts?.route,
    popover: {
      title: t(titleKey),
      description: t(descKey),
      side: opts?.side ?? 'bottom',
      align: opts?.align ?? 'start',
    },
  }
}

export function buildTourSteps(t: TFunction, isAdmin: boolean): TourStep[] {
  const steps: TourStep[] = [
    // ── Welcome ─────────────────────────────────────────────────────
    {
      popover: {
        title: t('tour.welcome.title'),
        description: t('tour.welcome.description'),
        side: 'over' as Side,
        align: 'center',
      },
    },

    // ── Sidebar navigation ──────────────────────────────────────────
    step('[data-tour="sidebar"]', 'tour.sidebar.title', 'tour.sidebar.description', t, { side: 'right', align: 'center' }),
    step('[data-tour="nav-events"]', 'tour.navEvents.title', 'tour.navEvents.description', t, { side: 'right' }),

    // ── Events page ─────────────────────────────────────────────────
    step('[data-tour="events-filter"]', 'tour.eventsFilter.title', 'tour.eventsFilter.description', t, { side: 'bottom', route: '/events' }),
    step('[data-tour="events-list"]', 'tour.eventsList.title', 'tour.eventsList.description', t, { side: 'top' }),

    // ── Live Tail ───────────────────────────────────────────────────
    step('[data-tour="nav-livetail"]', 'tour.navLiveTail.title', 'tour.navLiveTail.description', t, { side: 'right' }),
    step('[data-tour="livetail-controls"]', 'tour.livetailControls.title', 'tour.livetailControls.description', t, { side: 'bottom', route: '/live-tail' }),
    step('[data-tour="livetail-stream"]', 'tour.livetailStream.title', 'tour.livetailStream.description', t, { side: 'top' }),

    // ── Traces ──────────────────────────────────────────────────────
    step('[data-tour="nav-traces"]', 'tour.navTraces.title', 'tour.navTraces.description', t, { side: 'right' }),
    step('[data-tour="traces-filters"]', 'tour.tracesFilters.title', 'tour.tracesFilters.description', t, { side: 'bottom', route: '/traces' }),
    step('[data-tour="traces-list"]', 'tour.tracesList.title', 'tour.tracesList.description', t, { side: 'top' }),

    // ── Services ────────────────────────────────────────────────────
    step('[data-tour="nav-services"]', 'tour.navServices.title', 'tour.navServices.description', t, { side: 'right' }),
    step('[data-tour="services-graph"]', 'tour.servicesGraph.title', 'tour.servicesGraph.description', t, { side: 'top', route: '/services' }),

    // ── Signals ─────────────────────────────────────────────────────
    step('[data-tour="nav-signals"]', 'tour.navSignals.title', 'tour.navSignals.description', t, { side: 'right' }),
    step('[data-tour="signals-list"]', 'tour.signalsList.title', 'tour.signalsList.description', t, { side: 'top', route: '/signals' }),
    step('[data-tour="signals-create"]', 'tour.signalsCreate.title', 'tour.signalsCreate.description', t, { side: 'bottom' }),

    // ── Dashboards ──────────────────────────────────────────────────
    step('[data-tour="nav-dashboards"]', 'tour.navDashboards.title', 'tour.navDashboards.description', t, { side: 'right' }),
    step('[data-tour="dashboards-grid"]', 'tour.dashboardsGrid.title', 'tour.dashboardsGrid.description', t, { side: 'top', route: '/dashboards' }),
    step('[data-tour="dashboards-create"]', 'tour.dashboardsCreate.title', 'tour.dashboardsCreate.description', t, { side: 'bottom' }),

    // ── Alerts ──────────────────────────────────────────────────────
    step('[data-tour="nav-alerts"]', 'tour.navAlerts.title', 'tour.navAlerts.description', t, { side: 'right' }),
    step('[data-tour="alerts-list"]', 'tour.alertsList.title', 'tour.alertsList.description', t, { side: 'top', route: '/alerts' }),
    step('[data-tour="alerts-create"]', 'tour.alertsCreate.title', 'tour.alertsCreate.description', t, { side: 'bottom' }),

    // ── Anomalies ───────────────────────────────────────────────────
    step('[data-tour="nav-anomalies"]', 'tour.navAnomalies.title', 'tour.navAnomalies.description', t, { side: 'right' }),
    step('[data-tour="anomalies-list"]', 'tour.anomaliesList.title', 'tour.anomaliesList.description', t, { side: 'top', route: '/anomalies' }),

    // ── Health Checks ─────────────────────────────────────────────
    step('[data-tour="nav-healthchecks"]', 'tour.navHealthChecks.title', 'tour.navHealthChecks.description', t, { side: 'right' }),

    // ── Settings ────────────────────────────────────────────────────
    step('[data-tour="nav-settings"]', 'tour.navSettings.title', 'tour.navSettings.description', t, { side: 'right' }),
    step('[data-tour="settings-apikeys"]', 'tour.settingsApikeys.title', 'tour.settingsApikeys.description', t, { side: 'top', route: '/settings' }),
    step('[data-tour="settings-notifications"]', 'tour.settingsNotifications.title', 'tour.settingsNotifications.description', t, { side: 'top' }),
  ]

  // ── Admin-only steps ────────────────────────────────────────────
  if (isAdmin) {
    steps.push(
      step('[data-tour="nav-users"]', 'tour.navUsers.title', 'tour.navUsers.description', t, { side: 'right' }),
      step('[data-tour="nav-audit"]', 'tour.navAudit.title', 'tour.navAudit.description', t, { side: 'right' }),
      step('[data-tour="nav-ingestion"]', 'tour.navIngestion.title', 'tour.navIngestion.description', t, { side: 'right' }),
    )
  }

  // ── Help button + Finish ──────────────────────────────────────
  steps.push(
    step('[data-tour="header-help"]', 'tour.headerHelp.title', 'tour.headerHelp.description', t, { side: 'bottom', align: 'end' }),
    {
      popover: {
        title: t('tour.finish.title'),
        description: t('tour.finish.description'),
        side: 'over' as Side,
        align: 'center',
      },
    },
  )

  return steps
}
